import React, { useEffect, useState } from 'react';
import { FormattedMessage, injectIntl} from 'react-intl';
import { i18nIntl, isNotEmpty } from '../../lib/ep.js';

import BuilderCanvas from '../builder/BuilderCanvas';
import BuilderSidebar from '../builder/BuilderSidebar';
import Checkbox from '../builder/Checkbox';
import Model from '../builder/Model';
import { NavigationTabsBuilder } from '../builder/NavigationTabs';
import RichEditor from '../builder/RichEditor';
import Select from '../builder/Select';

import SegmentBuilder from './elements/SegmentBuilder.js';

import transferPlaceholder from '../../hoc/transferPlaceholder';
import SectionTitle from '../builder/SectionTitle.js';
import SectionSubtitle from '../builder/SectionSubtitle.js';
import ActionLink from '../builder/ActionLink.js';
import Message from '../builder/Message.js';
import BuilderMain from '../builder/BuilderMain.js';
import BuilderSubSectionTitle from './BuilderSubSectionTitle.js';
import Tooltip from '../builder/Tooltip.js';

const RichPlaceHolder = injectIntl(transferPlaceholder(RichEditor));

const Wrapper = (props) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	return (
		<BuilderMain pm={props?.promotion ?? {}}>
			{props.children}

			<BuilderCanvas is_adaptable={false} add_public_css_vars={false}>
				<div class="not_awesome gui_is_padded">
					<SectionTitle _class={{icon:'knk-entries-sided', first:true}}>User Segments</SectionTitle>

					<SectionSubtitle autotranslate={false}>{i18n("User Segments allow you to apply labels to the participants of the promotions to make groups of users. You can create several user segments in order to group them according to the criteria you need. In this way, you will be able to apply specific actions to the user segments you create.")} <ActionLink _href={`${window.zendesk_base_url}5848722065556`} _target="_blank">Learn how the User Segments system works in this tutorial</ActionLink>.</SectionSubtitle>

					<SectionSubtitle>Enable this section if you want to condition the participation of the stage to the fact that the user belongs or does not belong to specific segments.</SectionSubtitle>

					<SectionSubtitle>You can customize the message that users who have restricted access will see on the screen.</SectionSubtitle>
				</div>
			</BuilderCanvas>
		</BuilderMain>
	)
};

const StageSegmentsBuilder = (props) => {
	const stage = props?.stage ?? false;
	const promotion = props?.promotion ?? false;
	if(stage === false || promotion === false) return null;
	
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const is_draft = (promotion.status == '0') ? true : false;
	const is_multistage = promotion?.is_multistage ?? false;

	const [state, setState] = useState({ 
		pick_segment:(stage.segment_id != undefined) ? true : false,
		block_segment:(stage.block_segment_id != undefined) ? true : false,
		errors:{}
	});
	
	useEffect(()=>{
		if(!stage.segment_id || !stage.block_segment_id) return;
		if(isNotEmpty(stage, 'segment_id') && stage.segment_id.filter(value => stage.block_segment_id.includes(value)).length > 0) {
			setState((state) => ({ ...state, errors:{ block_segment:"Cannot allow and block the same segment" }}));
		} else if(Object.keys(state.errors).length > 0) {
			setState((state) => ({ ...state, errors:{} }));
		}
	}, [ stage.segment_id, stage.block_segment_id ]);

	const toggleSegment = (k, v,prop='segment_id') => {
		if(v) {
			setState({...state, [k]:true });
		} else {
			setState({...state, [k]:false });
			props.change(prop,'');
		}
	};

	const setSegment = (segment_id,prop='segment_id') => {
		let segments = stage[prop] || [];

		if(segments.includes(segment_id)) {
			segments = segments.filter((s) => s != segment_id);
			if(segments.length == 0) segments = "";
		} else {
			segments = [...segments,segment_id];
		}

		props.change(prop, segments);
	};

	const visibilities = [
		{ k:'0', v:"Hide the stage" },
		{ k:'1', v:"Disable the stage" },
	];

	const highest_plan = window?.type_in_plan ?? false;
	const is_corporate = window?.account_corporate ?? false;
	const has_min_plan = (window.has_subscription && ((highest_plan !== false && highest_plan == '2') || is_corporate)) ? true : false;

	if(!has_min_plan && is_draft) {
		return (
			<Wrapper promotion={props?.pm ?? {}} intl={props.intl}>
				<BuilderSidebar>
					<NavigationTabsBuilder
						sections={props.nav_sections}
						active={props.nav_active}
					>{props.stage_indicator}</NavigationTabsBuilder>

					<Message
						title="Plan required"
						_class={{ other:'in_top in_bottom' }}
					>
						<p>{i18n("With User Segments you can restrict the stage to a group of users.")}</p>

						<p><FormattedMessage id="Available to customers with an active {link}." values={{link:<ActionLink _href={`${window.panel_url}plans/add`} _target="_blank">White Label Plan</ActionLink>}} /></p>
					</Message>
				</BuilderSidebar>
			</Wrapper>
		)
	}

	if(!has_min_plan && !is_draft) {
		return (
			<Wrapper promotion={props?.pm ?? {}} intl={props.intl}>
				<BuilderSidebar>
					<NavigationTabsBuilder
						sections={props.nav_sections}
						active={props.nav_active}
					>{props.stage_indicator}</NavigationTabsBuilder>

					<Message
						title="Plan required"
						_class={{ other:'in_top in_bottom' }}
					>
						<p>{i18n("With User Segments you can restrict the stage to a group of users.")}</p>

						<p><FormattedMessage id="Available to customers with an active {link}." values={{link:i18n("White Label Plan")}} /> <FormattedMessage id="{link} if you want to upgrade your account to a monthly plan." values={{link:<ActionLink _href={`${window.web_base_url}contact/`} _target="_blank">Contact us</ActionLink>}} /></p>
					</Message>
				</BuilderSidebar>
			</Wrapper>
		)
	}

	return (
		<Wrapper promotion={props?.pm ?? {}} intl={props.intl}>
			<BuilderSidebar onSave={(cb) => props.saveStage(cb)} errors={{...props.errors,...state.errors}}>
				<NavigationTabsBuilder
					sections={props.nav_sections}
					active={props.nav_active}
				>{props.stage_indicator}</NavigationTabsBuilder>

				<article>
					<Checkbox
						_class={{ first:true }}
						name="pick_segment"
						model={state}
						change={toggleSegment}
						label_props={{
							tooltip:<Tooltip>This option allows you to restrict this stage so that only users who belong to some of the selected segments can participate.</Tooltip>,
						}}
					>Users must belong to one of the selected segments</Checkbox>

					{(state.pick_segment) && (
						<SegmentBuilder
							segmentSelected={setSegment}
							promotion_id={stage.promotion_id}
							selected={stage.segment_id}
						/>
					)}

					<Checkbox
						name="block_segment"
						model={state}
						change={(k, v) => toggleSegment(k, v, 'block_segment_id')}
						errors={state.errors}
						label_props={{
							tooltip:<Tooltip>This option allows you to restrict this stage to users who belong to any of the segments you select.</Tooltip>,
						}}
					>Users must not belong to any of the selected segments</Checkbox>

					{(state.block_segment) && (
						<SegmentBuilder
							segmentSelected={(k) => setSegment(k, 'block_segment_id')}
							promotion_id={stage.promotion_id}
							selected={stage.block_segment_id}
						/>
					)}
				</article>

				{(state.pick_segment || state.block_segment) && (
					<article class="gui_not_in_top_huge">
						<BuilderSubSectionTitle
							title="Restricted access settings"
							icon='knk-zipped'
						/>

						<Model model={stage} change={props.change}>
							<RichPlaceHolder
								name="disabled_message"
								placeholder="You don't have permissions to access this stage as it is restricted."
							>Message</RichPlaceHolder>

							{(is_multistage) && (
								<Select
									name="segment_visibility"
									values={visibilities}
								>What do you want to do in case of users who don't belong to the segment?</Select>
							)}
						</Model>
					</article>
				)}
			</BuilderSidebar>
		</Wrapper>
	)
}
export default injectIntl(StageSegmentsBuilder);